// MultiSelectDropdown.tsx
import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

interface Option {
  value: string;
  name: string;
}

interface MultiSelectDropdownProps {
  options: Option[];
  name: string;
  required?: boolean;
  className?: string;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  name,
  required = false,
  className,
}) => {
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  // Close the dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Update the hidden input's value whenever selectedValues changes
  useEffect(() => {
    if (hiddenInputRef.current) {
      hiddenInputRef.current.value = JSON.stringify(selectedValues);
    }
  }, [selectedValues]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionChange = (value: string) => {
    let newSelectedValues: string[];

    if (selectedValues.includes(value)) {
      newSelectedValues = selectedValues.filter((item) => item !== value);
    } else {
      newSelectedValues = [...selectedValues, value];
    }

    setSelectedValues(newSelectedValues);
  };

  const handleRemoveOption = (value: string) => {
    const newSelectedValues = selectedValues.filter((item) => item !== value);
    setSelectedValues(newSelectedValues);
  };

  return (
    <div
      className={`relative block w-full ${className || ""}`}
      ref={dropdownRef}
      onMouseLeave={() => setIsOpen(false)}
    >
      {/* Hidden input field for form submission */}
      <input
        type="hidden"
        name={name}
        ref={hiddenInputRef}
        value={JSON.stringify(selectedValues)}
        required={required}
      />

      {/* Display Area */}
      <div
        className="flex items-center flex-wrap border border-[#e3e3de] rounded-md bg-[#fcfbf8] cursor-pointer w-full"
        onClick={handleToggle}
      >
        {/* Adjusted padding and spacing */}
        <div className="flex flex-wrap items-center w-full px-2 py-1">
          {selectedValues.length > 0 ? (
            selectedValues.map((value, index) => {
              const option = options.find((opt) => opt.value === value);
              return (
                <div
                  key={`${value}-${index}`}
                  className="flex items-center bg-[#ebebe5] px-2 py-1 rounded-sm mr-2 mb-1"
                >
                  <span className="ml-1 mr-1 text-[#04262b]">
                    {option?.name || value}
                  </span>
                  <FontAwesomeIcon
                    icon={faTimes}
                    className="text-[#04262b] hover:text-red-600 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveOption(value);
                    }}
                  />
                </div>
              );
            })
          ) : (
            <span className="text-[#797e7c]">Select options</span>
          )}
          <div className="flex-grow"></div>
          <FontAwesomeIcon
            icon={isOpen ? faChevronUp : faChevronDown}
            className="text-[#04262b]"
          />
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute left-0 right-0 mt-0 bg-[#fcfbf8] border border-[#e3e3de] rounded-b-md shadow z-10 transition-transform duration-100 transform scale-90 origin-top animate-dropdown w-full">
          <ul className="max-h-60 overflow-y-auto">
            {options.map(({ value, name }, index) => (
              <li
                key={`${value}-${index}`}
                className="flex items-center px-4 py-2 hover:bg-[#ebebe5]"
              >
                <label className="flex items-center cursor-pointer w-full">
                  <input
                    type="checkbox"
                    value={value}
                    checked={selectedValues.includes(value)}
                    onChange={() => handleOptionChange(value)}
                    className="form-checkbox h-4 w-4 text-[#04262b]"
                  />
                  <span className="ml-2 text-[#04262b]">{name}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
